import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FetchResult } from '@apollo/client';
import { Button, Input, Table, H3 } from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';
import { Column } from 'react-table';

import { LocalizationContext } from 'localization';
import {
  ESettingsAliases,
  SettingsContext,
  TBlacklistItem,
  EBlacklistType,
  ISetSettingResponse,
} from 'settings';
import { shallowEqual } from 'utils';

import s from './Blacklist.module.scss';

interface IFormValues {
  name: string;
  class: string;
}

const DEFAULT_VALUES: IFormValues = {
  name: '',
  class: '',
};

export const Blacklist: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      isLoading,
      settings: { blacklist },
    },
    actions: { setSetting },
  } = useContext(SettingsContext);
  const [tab] = useState<EBlacklistType>(EBlacklistType.WINDOW);

  // const TABS: Array<ITabOption<EBlacklistType>> = [
  //   // { value: EBlacklistType.APPLICATION, title: t('setting.blacklist.app') },
  //   {
  //     value: EBlacklistType.WINDOW,
  //     title: t('setting.blacklist.window'),
  //   },
  //   // { value: EBlacklistType.TRAY, title: t('setting.blacklist.tray'), disabled: true },
  // ];

  const PLACEHOLDERS_MAP: Record<EBlacklistType, string> = {
    [EBlacklistType.APPLICATION]: t('setting.blacklist.app.placeholder'),
    [EBlacklistType.WINDOW]: t('setting.blacklist.window.placeholder'),
    [EBlacklistType.TRAY]: t('setting.blacklist.tray.placeholder'),
  };

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<IFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  const [selected, setSelected] = useState<TBlacklistItem | null>(null);

  const deleteBlacklistItem = useCallback(
    (
      blacklistItem: TBlacklistItem
    ): Promise<FetchResult<ISetSettingResponse>> =>
      setSetting({
        alias: ESettingsAliases.DEFENCE_BLACKLIST,
        value: JSON.stringify([
          ...blacklist.filter((item) => !shallowEqual(item, blacklistItem)),
        ]),
      }),
    [blacklist, setSetting]
  );

  useEffect(() => {
    setSelected(null);
  }, [tab, blacklist]);

  useEffect(() => {
    if (selected) {
      reset({ ...selected });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [selected, blacklist]);

  const COLUMNS = useMemo<Array<Column<TBlacklistItem>>>(
    () => [
      { accessor: 'name', Header: t('title') },
      { accessor: 'class', Header: t('setting.blacklist.class') },
      {
        accessor: 'type',
        Cell: ({ row: { original } }) => (
          <Button
            variant="ghost"
            className={s['remove']}
            onClick={() => deleteBlacklistItem(original)}
            iconName="BucketIcon"
          />
        ),
        disableResizing: true,
        disableSortBy: true,
        width: 78,
      },
    ],
    [deleteBlacklistItem, t]
  );

  const filteredData = useMemo(
    () => blacklist.filter((item) => item.type === tab),
    [tab, blacklist]
  );

  return (
    <div className={s.container}>
      <H3>{t('settings.shell.security.blacklist.title')}</H3>
      <span className={s.subtitle}>
        {t('settings.shell.security.blacklist.description')}
      </span>
      <form
        className={s.form}
        onSubmit={handleSubmit((values) => {
          const newItem = { type: tab, ...values };
          const newData = [
            ...blacklist.filter(
              (item) => !shallowEqual(item, selected || newItem)
            ),
          ];

          newData.push(newItem);

          return setSetting({
            alias: ESettingsAliases.DEFENCE_BLACKLIST,
            value: JSON.stringify(newData),
          });
        })}
      >
        <div className={s.fields}>
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                variant="secondary"
                error={error?.message}
                placeholder={t('title.insert')}
                disabled={isLoading || isSubmitting}
                bordered
              />
            )}
            name="name"
            rules={{
              validate: (value) => {
                if (!getValues('class') && !value) {
                  return t(
                    tab === EBlacklistType.WINDOW
                      ? 'setting.blacklist.fillField'
                      : 'title.insert'
                  );
                }
              },
            }}
            control={control}
          />
          {tab === EBlacklistType.WINDOW && (
            <Controller
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  variant="secondary"
                  error={error?.message}
                  placeholder={t('setting.blacklist.orClass')}
                  disabled={isLoading || isSubmitting}
                  bordered
                />
              )}
              name="class"
              rules={{
                validate: (value) => {
                  if (!getValues('name') && !value) {
                    return t('setting.blacklist.fillField');
                  }
                },
              }}
              control={control}
            />
          )}
          <Button type="submit" isLoading={isLoading || isSubmitting}>
            {t(selected ? 'save' : 'add')}
          </Button>
        </div>
      </form>
      <Table
        // headerBlock={<Tabs options={TABS} value={tab} onChange={setTab} />}
        className={s.table}
        data={filteredData}
        columns={COLUMNS}
        isLoading={isLoading}
        emptyDataPlaceholder={PLACEHOLDERS_MAP[tab]}
        hiddenColumns={tab === EBlacklistType.WINDOW ? [] : ['class']}
        getRowClassName={() => s.row}
        onRowClick={(event, row, table) => {
          if (isLoading) return;

          if (row.isSelected) {
            setSelected(null);
          } else {
            setSelected(row.original);
          }

          table.toggleAllRowsSelected(false);
          row.toggleRowSelected(!row.isSelected);
        }}
      />
    </div>
  );
};
